// src/components/ClientPortalInvoiceDetail.js

import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, Download, ExternalLink } from 'lucide-react';
import { toast } from 'react-toastify';
import { getClientInvoiceDetail } from '../functions/api_fethcers';
import { useClientInvoices } from '../hooks/useInvoices';
import { useClientDocs } from '../hooks/useClientDocs';
import PaymentModal from './PaymentModal';
import { StatusBadge, PrimaryButton, SecondaryButton, Spinner } from './portal/PortalUI';

const ClientPortalInvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showPayment, setShowPayment] = useState(false);

  const {
    data: invoice,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(['clientInvoiceDetail', id], () => getClientInvoiceDetail(id), {
    enabled: !!id,
  });

  const { createPaymentIntent, isCreatingPaymentIntent, invalidateInvoices } =
    useClientInvoices();

  const {
    downloadDocument,
    isDownloading,
    getShareableLink,
    isFetchingShareableLink,
  } = useClientDocs();

  const handleDownload = async () => {
    if (!invoice?.document_id) return;
    try {
      await downloadDocument(invoice.document_id);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleOpenExternalLink = async () => {
    if (!invoice?.document_id) return;
    // Open the tab synchronously, inside the click's gesture chain, so iOS
    // Safari and other mobile popup blockers don't kill it.
    const viewer = window.open('', '_blank');
    try {
      const data = await getShareableLink(invoice.document_id);
      if (data && data.share_url) {
        if (viewer) viewer.location = data.share_url;
      } else {
        toast.error('Shareable link not available');
        if (viewer) viewer.close();
      }
    } catch (err) {
      console.error('Error fetching shareable link:', err);
      if (viewer) viewer.close();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spinner label="Loading invoice…" />
      </div>
    );
  }

  if (isError || !invoice) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-center">
        <p className="text-red-500 mb-4">
          {error?.message || 'Invoice not found'}
        </p>
        <Link
          to="/client-invoices"
          className="text-grayteck-700 hover:text-grayteck-900 dark:text-grayteck-300 dark:hover:text-grayteck-100 font-semibold inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Invoices
        </Link>
      </div>
    );
  }

  const canPay = invoice.status !== 'paid' && invoice.status !== 'canceled';

  return (
    <div className="max-w-4xl mx-auto py-4">
      <button
        onClick={() => navigate('/client-invoices')}
        className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-grayteck-600 hover:text-grayteck-900 dark:text-grayteck-400 dark:hover:text-grayteck-200"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Invoices
      </button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-darkbg-800 rounded-xl border border-grayteck-200 dark:border-darkbg-600 shadow-card dark:shadow-card-dark p-4 sm:p-8"
      >
        <div className="flex justify-between items-start gap-3 mb-6 sm:mb-8 pb-6 border-b border-grayteck-200 dark:border-darkbg-600">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-caps text-grayteck-600 dark:text-grayteck-400 mb-1">
              Invoice
            </p>
            <h2 className="font-display text-xl sm:text-[26px] font-bold text-grayteck-900 dark:text-grayteck-100 break-words tracking-tight">
              #{invoice.invoice_number}
            </h2>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1">
              {invoice.issue_date && (
                <p className="text-sm text-grayteck-700 dark:text-grayteck-400">
                  Issued {new Date(invoice.issue_date).toLocaleDateString()}
                </p>
              )}
              {invoice.due_date && (
                <p className="text-sm text-grayteck-700 dark:text-grayteck-400">
                  Due {new Date(invoice.due_date).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          <StatusBadge
            status={invoice.status}
            label={invoice.status === 'opened' ? 'Pending' : invoice.status}
            className="capitalize flex-shrink-0"
          />
        </div>

        {/* Items Table */}
        <div className="overflow-x-auto -mx-4 sm:mx-0 mb-6 sm:mb-8">
          <table className="w-full min-w-[320px]">
            <thead>
              <tr className="border-b border-grayteck-200 dark:border-darkbg-600">
                <th className="text-left pb-3 px-2 sm:px-0 text-[11px] font-bold uppercase tracking-caps text-grayteck-600 dark:text-grayteck-400">
                  Description
                </th>
                <th className="text-right pb-3 px-1 sm:px-0 text-[11px] font-bold uppercase tracking-caps text-grayteck-600 dark:text-grayteck-400">
                  Qty
                </th>
                <th className="text-right pb-3 px-1 sm:px-0 text-[11px] font-bold uppercase tracking-caps text-grayteck-600 dark:text-grayteck-400">
                  Price
                </th>
                <th className="text-right pb-3 px-2 sm:px-0 text-[11px] font-bold uppercase tracking-caps text-grayteck-600 dark:text-grayteck-400">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-grayteck-200 dark:divide-darkbg-600">
              {(invoice.items || []).map((item, index) => (
                <tr key={index}>
                  <td className="py-3 sm:py-3.5 px-2 sm:px-0 text-sm text-grayteck-900 dark:text-grayteck-100">
                    {item.description}
                  </td>
                  <td className="text-right py-3 sm:py-3.5 px-1 sm:px-0 text-sm text-grayteck-700 dark:text-grayteck-400 tabular-nums">
                    {item.quantity}
                  </td>
                  <td className="text-right py-3 sm:py-3.5 px-1 sm:px-0 text-sm text-grayteck-700 dark:text-grayteck-400 tabular-nums">
                    ${item.unit_price?.toFixed(2)}
                  </td>
                  <td className="text-right py-3 sm:py-3.5 px-2 sm:px-0 text-sm font-semibold text-grayteck-900 dark:text-grayteck-100 tabular-nums">
                    ${item.amount?.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end mb-8">
          <div className="w-full sm:w-72 rounded-lg bg-grayteck-50 dark:bg-darkbg-700 border border-grayteck-200 dark:border-darkbg-600 p-4 space-y-2">
            <div className="flex justify-between text-sm text-grayteck-700 dark:text-grayteck-400">
              <span>Subtotal</span>
              <span className="tabular-nums">${invoice.subtotal?.toFixed(2)}</span>
            </div>
            {invoice.discount_percentage > 0 && (
              <div className="flex justify-between text-sm text-emerald-700 dark:text-emerald-400">
                <span>Discount ({invoice.discount_percentage}%)</span>
                <span className="tabular-nums">
                  -${((invoice.subtotal * invoice.discount_percentage) / 100).toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm text-grayteck-700 dark:text-grayteck-400">
              <span>Tax</span>
              <span className="tabular-nums">${invoice.tax?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-baseline pt-2.5 mt-1 border-t-2 border-gold-400">
              <span className="text-sm font-bold uppercase tracking-caps text-grayteck-700 dark:text-grayteck-300">
                Total
              </span>
              <span className="font-display text-xl font-bold text-grayteck-900 dark:text-grayteck-100 tabular-nums">
                ${invoice.total?.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {invoice.notes && (
          <div className="mb-8 rounded-lg bg-grayteck-50 dark:bg-darkbg-700 border-l-2 border-grayteck-400 dark:border-grayteck-600 px-4 py-3">
            <h3 className="text-[11px] font-bold uppercase tracking-caps text-grayteck-600 dark:text-grayteck-400 mb-1">
              Notes
            </h3>
            <p className="text-sm text-grayteck-700 dark:text-grayteck-300">
              {invoice.notes}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap justify-end gap-2 sm:gap-3 pt-5 border-t border-grayteck-200 dark:border-darkbg-600">
          {invoice.document_id && (
            <>
              <SecondaryButton
                onClick={handleOpenExternalLink}
                disabled={isFetchingShareableLink}
              >
                <ExternalLink className="w-4 h-4" /> View
              </SecondaryButton>
              <SecondaryButton onClick={handleDownload} disabled={isDownloading}>
                <Download className="w-4 h-4" /> Download
              </SecondaryButton>
            </>
          )}
          {canPay && (
            <PrimaryButton onClick={() => setShowPayment(true)} className="px-6">
              <CreditCard className="w-4 h-4" /> Pay Invoice
            </PrimaryButton>
          )}
        </div>
      </motion.div>

      <PaymentModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        invoice={invoice}
        createPaymentIntent={createPaymentIntent}
        isCreatingPaymentIntent={isCreatingPaymentIntent}
        onPaymentSuccess={() => {
          invalidateInvoices();
          refetch();
          toast.success('Payment successful!');
          setShowPayment(false);
        }}
      />
    </div>
  );
};

export default ClientPortalInvoiceDetail;
